import { AnimatePresence, motion } from "framer-motion";
import { Heart, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useWishlist } from "./WishlistContext";
import { inr } from "@/data/products";

const DENIM = "url('/img/denim/denim-texture.jpg')";

export default function WishlistDrawer() {
    const { items, open, setOpen, remove } = useWishlist();
    return (
        <>
            <button
                data-testid="wishlist-fab"
                onClick={() => setOpen(!open)}
                aria-label="Saved pieces"
                className="rivet fixed bottom-[6.4rem] left-5 z-[65] grid h-12 w-12 place-items-center rounded-full"
            >
                <Heart
                    size={17}
                    fill={items.length ? "currentColor" : "none"}
                    className="relative z-10 text-[#3c1e06]"
                />
                {items.length > 0 && (
                    <motion.span
                        key={items.length}
                        initial={{ scale: 1.5 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        data-testid="wishlist-count"
                        className="absolute -right-1 -top-1 z-10 grid h-5 w-5 place-items-center rounded-full bg-gold font-mono text-[10px] font-bold text-black"
                    >
                        {items.length}
                    </motion.span>
                )}
            </button>
            <AnimatePresence>
                {open && (
                    <>
                        <motion.div
                            className="fixed inset-0 z-[91] bg-black/60 backdrop-blur-sm"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setOpen(false)}
                        />
                        <motion.aside
                            data-testid="wishlist-drawer"
                            style={{ backgroundImage: DENIM }}
                            className="denim-bg fixed right-0 top-0 z-[92] h-full w-[400px] max-w-[92vw] border-l border-white/10"
                            initial={{ x: "106%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "106%" }}
                            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                        >
                            <div className="absolute inset-0 bg-obsidian/70" />
                            <div className="pointer-events-none absolute inset-3 rounded-b-[44px] border-2 border-dashed border-gold/50" />
                            <div className="relative z-10 flex h-full flex-col p-7">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gold">The Rivet Bar</p>
                                        <h3 className="mt-1 font-display text-xl font-bold uppercase text-ink">
                                            Saved Pieces {items.length > 0 && `(${items.length})`}
                                        </h3>
                                    </div>
                                    <button
                                        data-testid="wishlist-drawer-close"
                                        onClick={() => setOpen(false)}
                                        aria-label="Close wishlist"
                                        className="rivet grid h-10 w-10 place-items-center rounded-full"
                                    >
                                        <X size={15} className="relative z-10 text-[#3c1e06]" />
                                    </button>
                                </div>
                                {items.length === 0 ? (
                                    <div className="flex flex-1 flex-col items-center justify-center text-center" data-testid="wishlist-empty">
                                        <Heart size={26} className="text-gold/60" />
                                        <p className="mt-4 font-display text-lg font-bold uppercase text-ink">Nothing pinned yet.</p>
                                        <p className="mt-2 max-w-[230px] text-sm leading-relaxed text-mute">
                                            Tap the heart on any piece and it gets pinned here.
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="mt-6 flex-1 space-y-3 overflow-y-auto pr-1">
                                            <AnimatePresence initial={false}>
                                                {items.map((it) => (
                                                    <motion.div
                                                        key={it.productId}
                                                        initial={{ y: -30, opacity: 0 }}
                                                        animate={{ y: 0, opacity: 1 }}
                                                        exit={{ x: 60, opacity: 0 }}
                                                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                                                        className="flex items-center gap-4 border border-white/10 bg-black/40 p-3 backdrop-blur"
                                                        data-testid={`wishlist-item-${it.productId}`}
                                                    >
                                                        <img src={it.img} alt={it.name} className="h-16 w-14 shrink-0 object-cover" />
                                                        <div className="min-w-0 flex-1">
                                                            <p className="truncate font-display text-sm font-semibold text-ink">{it.name}</p>
                                                            <p className="mt-1 font-mono text-sm text-gold">{inr(it.price)}</p>
                                                        </div>
                                                        <button
                                                            data-testid={`wishlist-remove-${it.productId}`}
                                                            onClick={() => remove(it.productId)}
                                                            aria-label={`Remove ${it.name}`}
                                                            className="grid h-8 w-8 shrink-0 place-items-center border border-white/15 text-mute transition-colors hover:border-gold hover:text-gold"
                                                        >
                                                            <X size={13} />
                                                        </button>
                                                    </motion.div>
                                                ))}
                                            </AnimatePresence>
                                        </div>
                                        <div className="border-t border-dashed border-gold/40 pt-5">
                                            <Link
                                                to="/shop"
                                                data-testid="wishlist-browse"
                                                onClick={() => setOpen(false)}
                                                className="block w-full border border-gold/50 py-3.5 text-center font-display text-sm font-bold uppercase tracking-[0.2em] text-gold transition-colors hover:bg-gold hover:text-black"
                                            >
                                                Browse the Collection
                                            </Link>
                                            <p className="mt-3 text-center text-[10px] font-mono uppercase tracking-[0.2em] text-mute">
                                                Pick your size on the collection page
                                            </p>
                                        </div>
                                    </>
                                )}
                            </div>
                        </motion.aside>
                    </>
                )}
            </AnimatePresence>
        </>
    );
}
